import {UserResource} from '@//common/api';
import {Availability, useAvailability} from '@/logged/features/header-menu/composables/useAvailability';
import {useNotifications} from '@/logged/features/notifications';
import {useUser} from '@/logged/features/user';
import {defineAppContext} from '@malt/vue-context';
import {Pinia} from 'pinia';
import {ProfilePreferences, useProfilePreferences} from '@/logged/features/aside-menu/composables/useProfilePreferences';
import {FreelancerCompany, useFreelancerCompany} from '@/logged/features/header-menu/composables/useFreelancerCompany';
import {NavbarState, useNavbarState} from '@/logged/features/aside-menu/stores/navbar-state.store';

type Entries = [
    'user',
    'context',
    'selectIdentity',
    'changeNavbarState',
    'notifications',
    'acknowledgeOnboarding',
    'getAvailability',
    'updateAvailability',
    'getProfilePreferences',
    'getFreelancerCompany',
];

export const {createEndpoint, loadApiContext, useAppContext, getEndpointUrl, endpointExists} = defineAppContext<{}, Entries>({
    baseUrl: '/navbar/api/registry',
    async onResolvedContext({createEndpoint, pinia}) {
        await loadUser(pinia, createEndpoint);
        await Promise.all([
            loadNavbarContext(createEndpoint, pinia),
            loadFreelancerCompany(createEndpoint, pinia),
            loadNotifications(),
            loadAvailability(createEndpoint, pinia),
            loadProfilePreferences(createEndpoint, pinia),
        ]);
    },
});

interface NavbarStateResource {
    state: NavbarState;
    isEnforcedIdentity: boolean;
}

async function loadNavbarContext(_createEndpoint: typeof createEndpoint, pinia: Pinia) {
    const navbarStateStore = useNavbarState(pinia);
    const getNavbarContext = _createEndpoint<NavbarStateResource>('context');
    try {
        const navbarContext = await getNavbarContext();
        navbarStateStore.$patch(navbarContext);
    } catch (e) {
        console.error('Cannot fetch navbar context');
    }
}

async function loadUser(pinia: Pinia, _createEndpoint: typeof createEndpoint) {
    const userStore = useUser(pinia);
    const getUser = _createEndpoint<UserResource>('user');
    try {
        const user = await getUser();
        userStore.$patch({user: user});
    } catch (e) {
        console.error('Cannot fetch User context');
    }
}

async function loadNotifications() {
    const notificationsStore = useNotifications();
    notificationsStore.startUpdating();
}

async function loadAvailability(_createEndpoint: typeof createEndpoint, pinia: Pinia) {
    const userStore = useUser(pinia);
    if (!endpointExists('getAvailability') || userStore.hasSelectedClientIdentity) {
        return;
    }

    const availabilityStore = useAvailability();
    const getAvailability = _createEndpoint<Availability>('getAvailability');
    try {
        availabilityStore.availability = await getAvailability();
        availabilityStore.checkUrlParams();
    } catch (e) {
        console.error('Cannot fetch Availability context');
    }
}

async function loadProfilePreferences(_createEndpoint: typeof createEndpoint, pinia: Pinia) {
    const userStore = useUser(pinia);
    if (!endpointExists('getProfilePreferences') || userStore.hasSelectedClientIdentity) {
        return;
    }

    const profilePreferencesStore = useProfilePreferences();
    const getProfilePreferences = _createEndpoint<ProfilePreferences>('getProfilePreferences');
    try {
        profilePreferencesStore.preferences = await getProfilePreferences();
    } catch (e) {
        console.error('Cannot fetch ProfilePreferences context');
    }
}

async function loadFreelancerCompany(_createEndpoint: typeof createEndpoint, pinia: Pinia) {
    if (!endpointExists('getFreelancerCompany')) {
        return;
    }

    const freelancerCompanyStore = useFreelancerCompany(pinia);
    const getFreelancerCompany = _createEndpoint<FreelancerCompany>('getFreelancerCompany');
    try {
        freelancerCompanyStore.freelancerCompany = await getFreelancerCompany();
    } catch (e) {
        console.error('Cannot fetch FreelancerCompany context');
    }
}
